import type { DevEventInboxMessageDto } from '@/api';
import FilterDropdown from '@/components/table/toolbar/filter-dropdown';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import { Box, Flex, Text } from '@lego/klik-ui';
import { ThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
  MaterialReactTable,
} from 'material-react-table';
import { useMemo, useState } from 'react';
import { useDevEventInbox } from '../dev-dashboard-queries';
import {
  type EventInboxOptionColumn,
  getEventInboxColumnOptions,
  toEventInboxFilter,
} from '../event-inbox-filters';
import { PROCESSED_OPTIONS, countActiveFilters } from '../price-row-filters';
import { ActiveFilterStatus } from './active-filter-status';
import {
  DateRangeFilter,
  type DateRangeFilterValue,
} from './price-row-column-filters';
import { StatusDot } from './status-dot';

// A type alias (not an interface) so it fits the Record<string, unknown> row type of FilterStatus.
type InboxRow = Pick<DevEventInboxMessageDto, keyof DevEventInboxMessageDto>;

const theme = createTheme({ palette: { mode: 'light' } });

const EMPTY_FILTER = {};

const cellText = (value: string | number | null | undefined) => {
  const text = value ?? '-';
  return (
    <Text className="body-xs-regular" isTruncated title={String(text)}>
      {text}
    </Text>
  );
};

const formatOptionalTime = (value: Date | null | undefined) =>
  value ? formatDateTimeDisplay(value) : '-';

/**
 * The newest 50 event inbox messages, with column filters and sorting. Works like the price rows table:
 * a filter loads the newest 50 messages that match it from the backend, and sorting changes only the
 * order of the loaded messages. The payload is not loaded.
 */
export const FilterableEventInboxTable = () => {
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  // Not memoized: material-react-table changes range filter values in place.
  const filter = toEventInboxFilter(columnFilters);
  const activeFilterCount = countActiveFilters(filter);

  // The unfiltered messages give filter options that the current filter hides.
  const newestMessages = useDevEventInbox(EMPTY_FILTER);
  const filteredMessages = useDevEventInbox(filter);
  const rows: InboxRow[] = filteredMessages.data ?? [];

  const optionSources = useMemo(
    () => [newestMessages.data ?? [], filteredMessages.data ?? []],
    [newestMessages.data, filteredMessages.data],
  );

  const optionColumn = (
    key: EventInboxOptionColumn,
    header: string,
  ): MRT_ColumnDef<InboxRow> => ({
    accessorKey: key,
    header,
    size: 220,
    Cell: ({ row }) => cellText(row.original[key]),
    Filter: ({ header: columnHeader }) => (
      <FilterDropdown
        options={getEventInboxColumnOptions(key, optionSources)}
        value={columnHeader.column.getFilterValue() as string[] | undefined}
        onChange={(selected) =>
          columnHeader.column.setFilterValue(
            selected.length > 0 ? selected : undefined,
          )
        }
      />
    ),
  });

  const columns: MRT_ColumnDef<InboxRow>[] = [
    {
      id: 'isProcessed',
      accessorFn: (row) => (row.isProcessed ? 1 : 0),
      header: 'Is Processed',
      size: 110,
      sortingFn: 'basic',
      Cell: ({ row }) => (
        <StatusDot
          isOn={row.original.isProcessed}
          onLabel="Processed"
          offLabel="Not processed"
        />
      ),
      Filter: ({ header: columnHeader }) => (
        <FilterDropdown
          options={PROCESSED_OPTIONS}
          value={columnHeader.column.getFilterValue() as string[] | undefined}
          onChange={(selected) =>
            columnHeader.column.setFilterValue(
              selected.length > 0 ? selected : undefined,
            )
          }
          singleSelect
        />
      ),
    },
    optionColumn('topic', 'Topic'),
    optionColumn('type', 'Type'),
    {
      id: 'messageTime',
      accessorFn: (row) => row.messageTime,
      header: 'Message Time',
      size: 220,
      sortingFn: 'datetime',
      Cell: ({ row }) => cellText(formatOptionalTime(row.original.messageTime)),
      Filter: ({ header: columnHeader }) => (
        <DateRangeFilter
          value={
            columnHeader.column.getFilterValue() as
              | DateRangeFilterValue
              | undefined
          }
          onChange={(value) => columnHeader.column.setFilterValue(value)}
          withTime
        />
      ),
    },
    {
      id: 'acknowledgedAt',
      accessorFn: (row) => row.acknowledgedAt ?? undefined,
      header: 'Acknowledged At',
      size: 170,
      sortingFn: 'datetime',
      enableColumnFilter: false,
      Cell: ({ row }) =>
        cellText(formatOptionalTime(row.original.acknowledgedAt)),
    },
    {
      accessorKey: 'sequence',
      header: 'Sequence',
      sortingFn: 'basic',
      enableColumnFilter: false,
      Cell: ({ row }) => cellText(row.original.sequence),
    },
    {
      id: 'subject',
      accessorFn: (row) => row.subject ?? '',
      header: 'Subject',
      size: 160,
      enableColumnFilter: false,
      Cell: ({ row }) => cellText(row.original.subject),
    },
    {
      accessorKey: 'messageId',
      header: 'Message Id',
      size: 160,
      enableColumnFilter: false,
      Cell: ({ row }) => cellText(row.original.messageId),
    },
    {
      accessorKey: 'id',
      header: 'Inbox Id',
      size: 160,
      enableColumnFilter: false,
      Cell: ({ row }) => cellText(row.original.id),
    },
  ];

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <ThemeProvider theme={theme}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          dateFormats={{ keyboardDate: 'DD/MM/YYYY' }}
        >
          <MaterialReactTable
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            manualFiltering
            onColumnFiltersChange={setColumnFilters}
            state={{
              columnFilters,
              showProgressBars: filteredMessages.isFetching,
            }}
            enableSorting
            enableStickyHeader
            enablePagination={false}
            enableColumnActions={false}
            enableGlobalFilter={false}
            enableBottomToolbar={false}
            enableColumnResizing
            layoutMode="grid"
            initialState={{ density: 'compact', showColumnFilters: true }}
            defaultColumn={{ size: 120, minSize: 70 }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
            muiTableContainerProps={{ sx: { flex: 1, minHeight: 0 } }}
            muiTableHeadCellProps={{
              sx: { fontSize: '11px', fontWeight: 600 },
            }}
            renderEmptyRowsFallback={() => (
              <Text className="body-xs-regular" padding={2}>
                {filteredMessages.isError
                  ? 'Could not load the data. It reloads automatically.'
                  : 'No messages match the filters.'}
              </Text>
            )}
            renderTopToolbar={({ table }) => (
              <Flex marginY="4px" gap="8px" alignItems="center">
                <MRT_ToggleFiltersButton table={table} size="small" />
                <ActiveFilterStatus
                  count={activeFilterCount}
                  onClear={() => table.resetColumnFilters()}
                />
              </Flex>
            )}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </Box>
  );
};
